import React, { useState, useEffect, useCallback } from 'react';
import { FiShield, FiPlus, FiEdit2, FiTrash2, FiUsers, FiClock, FiCheck, FiCalendar, FiSearch, FiChevronDown, FiChevronRight, FiX, FiBook, FiPlay, FiFilter, FiFileText } from 'react-icons/fi';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import {
  getToolboxTalkTopics, createToolboxTalkTopic, updateToolboxTalkTopic, deleteToolboxTalkTopic,
  getToolboxTalkSessions, createToolboxTalkSession,
  completeToolboxTalk, deleteToolboxTalkSession,
  getTeamMembers
} from '../services/api';

const TOPIC_CATEGORIES = ['General Safety', 'Surveying Field Safety', 'Environmental', 'Equipment'];
const SEASON_COLORS = {
  spring: { bg: '#dcfce7', color: '#166534', label: 'Spring' },
  summer: { bg: '#fef3c7', color: '#92400e', label: 'Summer' },
  fall: { bg: '#ffedd5', color: '#9a3412', label: 'Fall' },
  winter: { bg: '#dbeafe', color: '#1e40af', label: 'Winter' }
};
const CATEGORY_COLORS = {
  'General Safety': { bg: '#dbeafe', color: '#1d4ed8' },
  'Surveying Field Safety': { bg: '#dcfce7', color: '#166534' },
  'Environmental': { bg: '#fef3c7', color: '#92400e' },
  'Equipment': { bg: '#f3e8ff', color: '#7c3aed' }
};

function Safety() {
  const { user } = useAuth();
  const isAdmin = ['Admin', 'Owner', 'Office Manager'].includes(user?.role);

  const [activeTab, setActiveTab] = useState('manual');
  const [topics, setTopics] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentSeason, setCurrentSeason] = useState('');
  const [loading, setLoading] = useState(true);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [seasonFilter, setSeasonFilter] = useState(false);
  const [historyStatusFilter, setHistoryStatusFilter] = useState('');

  // Topic form
  const [showTopicForm, setShowTopicForm] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [topicForm, setTopicForm] = useState({ title: '', category: 'General Safety', content: '', duration_minutes: 15, season: '' });

  // Conduct talk modal
  const [showConductModal, setShowConductModal] = useState(false);
  const [conductSession, setConductSession] = useState(null);
  const [conductTopic, setConductTopic] = useState(null);
  const [conductPresenterId, setConductPresenterId] = useState('');
  const [conductAttendeeIds, setConductAttendeeIds] = useState([]);
  const [conductNotes, setConductNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Schedule form
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleTopicId, setScheduleTopicId] = useState('');
  const [schedulePresenterId, setSchedulePresenterId] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');

  // Detail views
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [topicsRes, sessionsRes, teamRes] = await Promise.all([
        getToolboxTalkTopics(),
        getToolboxTalkSessions(),
        getTeamMembers()
      ]);
      setTopics(topicsRes.data.topics || []);
      setCurrentSeason(topicsRes.data.current_season || '');
      setSessions(sessionsRes.data.sessions || []);
      const members = teamRes.data.team_members || teamRes.data || [];
      setTeamMembers(Array.isArray(members) ? members.filter(m => m.is_active !== false) : []);
    } catch (err) {
      console.error('Error loading safety data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Topic CRUD
  const handleSaveTopic = async () => {
    try {
      if (editingTopic) {
        await updateToolboxTalkTopic(editingTopic.id, topicForm);
      } else {
        await createToolboxTalkTopic(topicForm);
      }
      setShowTopicForm(false);
      setEditingTopic(null);
      setTopicForm({ title: '', category: 'General Safety', content: '', duration_minutes: 15, season: '' });
      loadData();
    } catch (err) {
      console.error('Error saving topic:', err);
      alert('Error saving topic');
    }
  };

  const handleEditTopic = (topic) => {
    setEditingTopic(topic);
    setTopicForm({
      title: topic.title,
      category: topic.category,
      content: topic.content || '',
      duration_minutes: topic.duration_minutes || 15,
      season: topic.season || ''
    });
    setShowTopicForm(true);
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Deactivate this topic?')) return;
    try {
      await deleteToolboxTalkTopic(topicId);
      loadData();
    } catch (err) {
      console.error('Error deleting topic:', err);
    }
  };

  // Quick Talk - create session and open conduct modal
  const handleQuickTalk = (topic) => {
    setConductTopic(topic);
    setConductSession(null);
    setConductPresenterId(user?.id || '');
    setConductAttendeeIds([]);
    setConductNotes('');
    setShowConductModal(true);
  };

  // Conduct a scheduled session
  const handleConductSession = (session) => {
    setConductSession(session);
    setConductTopic({ id: session.topic_id, title: session.topic_title, category: session.topic_category, content: session.topic_content, duration_minutes: session.topic_duration, season: session.topic_season });
    setConductPresenterId(session.presenter_id || user?.id || '');
    setConductAttendeeIds(session.attendees ? session.attendees.map(a => a.team_member_id) : []);
    setConductNotes(session.notes || '');
    setShowConductModal(true);
  };

  const handleCompleteTalk = async () => {
    if (conductAttendeeIds.length === 0) {
      alert('Please select at least one attendee');
      return;
    }
    setSubmitting(true);
    try {
      let sessionId = conductSession?.id;
      // If quick talk, create session first
      if (!sessionId) {
        const today = new Date().toISOString().split('T')[0];
        const res = await createToolboxTalkSession({
          topic_id: conductTopic.id,
          presenter_id: conductPresenterId || user?.id,
          scheduled_date: today
        });
        sessionId = res.data.id;
      }
      await completeToolboxTalk(sessionId, {
        presenter_id: conductPresenterId || user?.id,
        attendee_ids: conductAttendeeIds,
        notes: conductNotes
      });
      setShowConductModal(false);
      loadData();
    } catch (err) {
      console.error('Error completing talk:', err);
      alert('Error completing talk');
    } finally {
      setSubmitting(false);
    }
  };

  // Schedule talk
  const handleScheduleTalk = async () => {
    if (!scheduleTopicId || !scheduleDate) {
      alert('Please select a topic and date');
      return;
    }
    try {
      await createToolboxTalkSession({
        topic_id: parseInt(scheduleTopicId),
        presenter_id: schedulePresenterId ? parseInt(schedulePresenterId) : user?.id,
        scheduled_date: scheduleDate
      });
      setShowScheduleForm(false);
      setScheduleTopicId('');
      setSchedulePresenterId('');
      setScheduleDate('');
      loadData();
    } catch (err) {
      console.error('Error scheduling talk:', err);
      alert('Error scheduling talk');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await deleteToolboxTalkSession(sessionId);
      loadData();
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  // Filter topics
  const filteredTopics = topics.filter(t => {
    if (categoryFilter && t.category !== categoryFilter) return false;
    if (searchTerm && !t.title.toLowerCase().includes(searchTerm.toLowerCase()) && !t.content?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (seasonFilter && currentSeason) {
      if (!t.season) return true; // year-round always shows
      return t.season.includes(currentSeason);
    }
    return true;
  });

  // Filter sessions
  const filteredSessions = sessions.filter(s => {
    if (historyStatusFilter && s.status !== historyStatusFilter) return false;
    return true;
  });

  const upcomingSessions = sessions.filter(s => s.status === 'Scheduled');
  const completedThisWeek = sessions.filter(s => {
    if (s.status !== 'Completed' || !s.completed_date) return false;
    const completed = new Date(s.completed_date);
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(monday.getDate() - monday.getDay() + (monday.getDay() === 0 ? -6 : 1));
    monday.setHours(0, 0, 0, 0);
    return completed >= monday;
  });

  const isInSeason = (topic) => {
    if (!topic.season || !currentSeason) return false;
    return topic.season.includes(currentSeason);
  };

  const getSeasonBadges = (seasonStr) => {
    if (!seasonStr) return null;
    return seasonStr.split(',').map(s => {
      const sc = SEASON_COLORS[s.trim()];
      if (!sc) return null;
      return (
        <span key={s} style={{
          fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
          backgroundColor: sc.bg, color: sc.color, fontWeight: 600, marginLeft: '4px'
        }}>
          {sc.label}
        </span>
      );
    });
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading safety data...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiShield color="#2563eb" /> Toolbox Talks
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
            Safety meeting topics, scheduling, and attendance tracking
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {completedThisWeek.length > 0 ? (
            <span style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#dcfce7', color: '#166534', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FiCheck size={14} /> Talk completed this week
            </span>
          ) : (
            <span style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#fef3c7', color: '#92400e', fontSize: '13px', fontWeight: 600 }}>
              No talk this week yet
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '24px', borderBottom: '2px solid #e5e7eb' }}>
        {[
          { key: 'manual', label: 'Safety Manual', icon: <FiFileText size={16} /> },
          { key: 'topics', label: 'Topics Library', icon: <FiBook size={16} /> },
          { key: 'history', label: 'Talk History', icon: <FiClock size={16} /> },
          { key: 'schedule', label: 'Schedule', icon: <FiCalendar size={16} /> }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '12px 24px', fontSize: '15px', fontWeight: '600',
              border: 'none', background: 'none', cursor: 'pointer',
              color: activeTab === tab.key ? '#2563eb' : '#6b7280',
              borderBottom: activeTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px', display: 'flex', alignItems: 'center', gap: '8px'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ========== SAFETY MANUAL TAB ========== */}
      {activeTab === 'manual' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
            <h3 style={{ margin: 0, fontSize: '18px' }}><FiFileText /> Company Safety Manual</h3>
            <a
              href={`${API_URL}/safety/manual`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '8px 16px', backgroundColor: '#2563eb', color: 'white',
                borderRadius: '6px', textDecoration: 'none', fontSize: '14px',
                fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '6px'
              }}
            >
              <FiFileText size={14} /> Open in New Tab
            </a>
          </div>
          <iframe
            src={`${API_URL}/safety/manual`}
            title="Safety Manual"
            style={{ width: '100%', height: 'calc(100vh - 280px)', border: 'none' }}
          />
        </div>
      )}

      {/* ========== TOPICS TAB ========== */}
      {activeTab === 'topics' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
              <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Search topics..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', backgroundColor: 'white' }}
            >
              <option value="">All Categories</option>
              {TOPIC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              onClick={() => setSeasonFilter(!seasonFilter)}
              style={{
                padding: '10px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                backgroundColor: seasonFilter ? '#dbeafe' : 'white',
                color: seasonFilter ? '#1d4ed8' : '#374151',
                fontWeight: seasonFilter ? 600 : 400
              }}
            >
              <FiFilter size={14} /> In Season
              {currentSeason && <span style={{ fontSize: '11px', textTransform: 'capitalize' }}>({currentSeason})</span>}
            </button>
            {isAdmin && (
              <button
                onClick={() => { setEditingTopic(null); setTopicForm({ title: '', category: 'General Safety', content: '', duration_minutes: 15, season: '' }); setShowTopicForm(true); }}
                style={{
                  padding: '10px 16px', border: 'none', borderRadius: '8px', fontSize: '14px',
                  cursor: 'pointer', backgroundColor: '#2563eb', color: 'white', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                <FiPlus size={14} /> Custom Topic
              </button>
            )}
          </div>

          {/* Topic Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '12px' }}>
            {filteredTopics.map(topic => {
              const catColor = CATEGORY_COLORS[topic.category] || { bg: '#f3f4f6', color: '#374151' };
              const inSeason = isInSeason(topic);
              return (
                <div key={topic.id} style={{
                  backgroundColor: 'white', borderRadius: '12px', padding: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: inSeason ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  position: 'relative'
                }}>
                  {inSeason && (
                    <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}>
                      In Season
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 600, paddingRight: inSeason ? '70px' : '0' }}>{topic.title}</h3>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{
                          fontSize: '11px', padding: '2px 10px', borderRadius: '10px',
                          backgroundColor: catColor.bg, color: catColor.color, fontWeight: 600
                        }}>
                          {topic.category}
                        </span>
                        <span style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <FiClock size={12} /> {topic.duration_minutes} min
                        </span>
                        {topic.is_custom && (
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#f3f4f6', color: '#6b7280' }}>Custom</span>
                        )}
                        {getSeasonBadges(topic.season)}
                      </div>
                    </div>
                  </div>

                  {/* Content preview / full talk */}
                  {expandedTopic === topic.id ? (
                    <div style={{
                      margin: '8px 0', fontSize: '13px', color: '#374151', lineHeight: '1.6',
                      whiteSpace: 'pre-line', backgroundColor: '#f9fafb', padding: '12px',
                      borderRadius: '8px', border: '1px solid #e5e7eb'
                    }}>
                      {topic.content}
                    </div>
                  ) : (
                    <p style={{
                      margin: '8px 0', fontSize: '13px', color: '#6b7280', lineHeight: '1.4',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                    }}>
                      {topic.content?.split('\n').find(line => line.trim() && !line.startsWith('INTRODUCTION') && !line.startsWith('KEY POINTS')) || topic.content}
                    </p>
                  )}

                  {topic.content && topic.content.length > 120 && (
                    <button
                      onClick={() => setExpandedTopic(expandedTopic === topic.id ? null : topic.id)}
                      style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '13px', padding: 0, fontWeight: 500 }}
                    >
                      {expandedTopic === topic.id ? 'Show less' : 'Read full talk'}
                    </button>
                  )}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px', borderTop: '1px solid #f3f4f6', paddingTop: '12px' }}>
                    <button
                      onClick={() => handleQuickTalk(topic)}
                      style={{
                        flex: 1, padding: '8px 12px', border: 'none', borderRadius: '8px',
                        backgroundColor: '#2563eb', color: 'white', fontSize: '13px', fontWeight: 600,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}
                    >
                      <FiPlay size={12} /> Quick Talk
                    </button>
                    {isAdmin && topic.is_custom && (
                      <>
                        <button
                          onClick={() => handleEditTopic(topic)}
                          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}
                        >
                          <FiEdit2 size={12} />
                        </button>
                        <button
                          onClick={() => handleDeleteTopic(topic.id)}
                          style={{ padding: '8px 12px', border: '1px solid #fecaca', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}
                        >
                          <FiTrash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {filteredTopics.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
              No topics match your filters
            </div>
          )}
        </div>
      )}

      {/* ========== HISTORY TAB ========== */}
      {activeTab === 'history' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
            <select
              value={historyStatusFilter}
              onChange={e => setHistoryStatusFilter(e.target.value)}
              style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', backgroundColor: 'white' }}
            >
              <option value="">All Statuses</option>
              <option value="Completed">Completed</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>{filteredSessions.length} sessions</span>
          </div>

          {filteredSessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No talk sessions found</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredSessions.map(session => {
                const statusColors = {
                  Completed: { bg: '#dcfce7', color: '#166534' },
                  Scheduled: { bg: '#dbeafe', color: '#1d4ed8' },
                  Cancelled: { bg: '#fee2e2', color: '#991b1b' }
                };
                const sc = statusColors[session.status] || { bg: '#f3f4f6', color: '#374151' };
                const isExpanded = expandedSession === session.id;
                return (
                  <div key={session.id} style={{
                    backgroundColor: 'white', borderRadius: '12px', padding: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb'
                  }}>
                    <div
                      onClick={() => setExpandedSession(isExpanded ? null : session.id)}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
                    >
                      {isExpanded ? <FiChevronDown size={16} color="#6b7280" /> : <FiChevronRight size={16} color="#6b7280" />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '15px' }}>{session.topic_title}</div>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                          {session.scheduled_date && new Date(session.scheduled_date + 'T00:00:00').toLocaleDateString()}
                          {session.presenter_name && ` • Presented by ${session.presenter_name}`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {session.attendee_count > 0 && (
                          <span style={{ fontSize: '13px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiUsers size={14} /> {session.attendee_count}
                          </span>
                        )}
                        <span style={{
                          fontSize: '12px', padding: '4px 12px', borderRadius: '10px',
                          backgroundColor: sc.bg, color: sc.color, fontWeight: 600
                        }}>
                          {session.status}
                        </span>
                        {session.status === 'Scheduled' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleConductSession(session); }}
                            style={{
                              padding: '6px 12px', border: 'none', borderRadius: '6px',
                              backgroundColor: '#2563eb', color: 'white', fontSize: '12px', fontWeight: 600,
                              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                            }}
                          >
                            <FiPlay size={12} /> Conduct
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteSession(session.id); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
                        {session.topic_content && (
                          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', lineHeight: '1.5', backgroundColor: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
                            <strong>Talk Points:</strong> {session.topic_content}
                          </div>
                        )}
                        {session.notes && (
                          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                            <strong>Notes:</strong> {session.notes}
                          </div>
                        )}
                        {session.attendees && session.attendees.length > 0 && (
                          <div>
                            <strong style={{ fontSize: '13px', color: '#374151' }}>Attendees ({session.attendees.length}):</strong>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                              {session.attendees.map(a => (
                                <span key={a.id} style={{
                                  fontSize: '12px', padding: '4px 10px', borderRadius: '16px',
                                  backgroundColor: '#f3f4f6', color: '#374151'
                                }}>
                                  {a.team_member_name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {session.completed_date && (
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                            Completed: {new Date(session.completed_date).toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========== SCHEDULE TAB ========== */}
      {activeTab === 'schedule' && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <button
              onClick={() => setShowScheduleForm(true)}
              style={{
                padding: '10px 20px', border: 'none', borderRadius: '8px',
                backgroundColor: '#2563eb', color: 'white', fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              <FiPlus size={14} /> Schedule a Talk
            </button>
          </div>

          {/* Upcoming */}
          <h3 style={{ fontSize: '16px', color: '#374151', marginBottom: '12px' }}>
            Upcoming Scheduled Talks ({upcomingSessions.length})
          </h3>
          {upcomingSessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#9ca3af', backgroundColor: '#f9fafb', borderRadius: '12px', marginBottom: '20px' }}>
              No upcoming talks scheduled
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {upcomingSessions.map(session => (
                <div key={session.id} style={{
                  backgroundColor: 'white', borderRadius: '12px', padding: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb',
                  display: 'flex', alignItems: 'center', gap: '12px'
                }}>
                  <div style={{ width: '50px', height: '50px', borderRadius: '12px', backgroundColor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FiCalendar size={20} color="#2563eb" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{session.topic_title}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {session.scheduled_date && new Date(session.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                      {session.presenter_name && ` • ${session.presenter_name}`}
                    </div>
                  </div>
                  <button
                    onClick={() => handleConductSession(session)}
                    style={{
                      padding: '8px 16px', border: 'none', borderRadius: '8px',
                      backgroundColor: '#2563eb', color: 'white', fontSize: '13px', fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                  >
                    <FiPlay size={12} /> Conduct Now
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDeleteSession(session.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '8px' }}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* This Week Status */}
          <div style={{
            backgroundColor: completedThisWeek.length > 0 ? '#f0fdf4' : '#fffbeb',
            borderRadius: '12px', padding: '20px',
            border: `1px solid ${completedThisWeek.length > 0 ? '#bbf7d0' : '#fde68a'}`
          }}>
            <h3 style={{ fontSize: '16px', color: '#374151', marginTop: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {completedThisWeek.length > 0 ? <FiCheck color="#16a34a" /> : <FiShield color="#d97706" />}
              This Week
            </h3>
            {completedThisWeek.length > 0 ? (
              <div>
                {completedThisWeek.map(s => (
                  <div key={s.id} style={{ fontSize: '14px', color: '#166534' }}>
                    "{s.topic_title}" — presented by {s.presenter_name} with {s.attendee_count} attendee{s.attendee_count !== 1 ? 's' : ''}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
                No toolbox talk has been completed this week. Schedule or conduct one now.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ========== TOPIC FORM MODAL ========== */}
      {showTopicForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '95%', maxWidth: '550px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>{editingTopic ? 'Edit Topic' : 'Create Custom Topic'}</h2>
              <button onClick={() => { setShowTopicForm(false); setEditingTopic(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <FiX size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Title *</label>
                <input
                  type="text"
                  value={topicForm.title}
                  onChange={e => setTopicForm({ ...topicForm, title: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  placeholder="e.g., Ladder Safety"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Category *</label>
                <select
                  value={topicForm.category}
                  onChange={e => setTopicForm({ ...topicForm, category: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                >
                  {TOPIC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Talk Points / Content</label>
                <textarea
                  value={topicForm.content}
                  onChange={e => setTopicForm({ ...topicForm, content: e.target.value })}
                  rows={5}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                  placeholder="Key talking points for this safety topic..."
                />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Duration (minutes)</label>
                  <input
                    type="number"
                    value={topicForm.duration_minutes}
                    onChange={e => setTopicForm({ ...topicForm, duration_minutes: parseInt(e.target.value) || 15 })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Season(s)</label>
                  <select
                    value={topicForm.season}
                    onChange={e => setTopicForm({ ...topicForm, season: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  >
                    <option value="">Year-round</option>
                    <option value="spring">Spring</option>
                    <option value="summer">Summer</option>
                    <option value="fall">Fall</option>
                    <option value="winter">Winter</option>
                    <option value="spring,summer">Spring & Summer</option>
                    <option value="fall,winter">Fall & Winter</option>
                    <option value="spring,summer,fall">Spring, Summer & Fall</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowTopicForm(false); setEditingTopic(null); }}
                style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTopic}
                disabled={!topicForm.title || !topicForm.category}
                style={{
                  padding: '10px 20px', border: 'none', borderRadius: '8px',
                  backgroundColor: topicForm.title && topicForm.category ? '#2563eb' : '#9ca3af',
                  color: 'white', cursor: topicForm.title && topicForm.category ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: 600
                }}
              >
                {editingTopic ? 'Save Changes' : 'Create Topic'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== SCHEDULE FORM MODAL ========== */}
      {showScheduleForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '95%', maxWidth: '500px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Schedule a Talk</h2>
              <button onClick={() => setShowScheduleForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <FiX size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Topic *</label>
                <select
                  value={scheduleTopicId}
                  onChange={e => setScheduleTopicId(e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                >
                  <option value="">Select a topic...</option>
                  {TOPIC_CATEGORIES.map(cat => {
                    const catTopics = topics.filter(t => t.category === cat);
                    if (catTopics.length === 0) return null;
                    return (
                      <optgroup key={cat} label={cat}>
                        {catTopics.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.title} ({t.duration_minutes} min){t.season && isInSeason(t) ? ' ★' : ''}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Presenter</label>
                <select
                  value={schedulePresenterId}
                  onChange={e => setSchedulePresenterId(e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                >
                  <option value="">Myself</option>
                  {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Date *</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowScheduleForm(false)}
                style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleTalk}
                disabled={!scheduleTopicId || !scheduleDate}
                style={{
                  padding: '10px 20px', border: 'none', borderRadius: '8px',
                  backgroundColor: scheduleTopicId && scheduleDate ? '#2563eb' : '#9ca3af',
                  color: 'white', cursor: scheduleTopicId && scheduleDate ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: 600
                }}
              >
                Schedule Talk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== CONDUCT TALK MODAL ========== */}
      {showConductModal && conductTopic && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '95%', maxWidth: '650px', maxHeight: 'calc(90dvh - 90px)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiShield color="#2563eb" /> Conduct Safety Talk
              </h2>
              <button onClick={() => setShowConductModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <FiX size={20} />
              </button>
            </div>

            {/* Topic Info */}
            <div style={{ backgroundColor: '#eff6ff', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e40af' }}>{conductTopic.title}</h3>
                {conductTopic.season && getSeasonBadges(conductTopic.season)}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                {conductTopic.category} • {conductTopic.duration_minutes} min
              </div>
              {conductTopic.content && (
                <div style={{
                  fontSize: '14px', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-line',
                  maxHeight: '300px', overflowY: 'auto', backgroundColor: 'white',
                  padding: '12px', borderRadius: '8px', border: '1px solid #dbeafe', marginTop: '8px'
                }}>
                  {conductTopic.content}
                </div>
              )}
            </div>

            {/* Presenter */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Presenter</label>
              <select
                value={conductPresenterId}
                onChange={e => setConductPresenterId(parseInt(e.target.value))}
                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              >
                {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>

            {/* Attendees */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                  Attendees ({conductAttendeeIds.length}/{teamMembers.length})
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setConductAttendeeIds(teamMembers.map(m => m.id))}
                    style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setConductAttendeeIds([])}
                    style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div style={{
                border: '1px solid #d1d5db', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto',
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))'
              }}>
                {teamMembers.map(m => {
                  const checked = conductAttendeeIds.includes(m.id);
                  return (
                    <label key={m.id} style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                      cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                      backgroundColor: checked ? '#eff6ff' : 'white'
                    }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setConductAttendeeIds(prev =>
                            checked ? prev.filter(id => id !== m.id) : [...prev, m.id]
                          );
                        }}
                        style={{ accentColor: '#2563eb' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: checked ? 600 : 400 }}>{m.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Notes (optional)</label>
              <textarea
                value={conductNotes}
                onChange={e => setConductNotes(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                placeholder="Any additional notes about this safety talk..."
              />
            </div>

            {/* Submit */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConductModal(false)}
                style={{ padding: '12px 20px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteTalk}
                disabled={conductAttendeeIds.length === 0 || submitting}
                style={{
                  padding: '12px 24px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                  backgroundColor: conductAttendeeIds.length > 0 && !submitting ? '#16a34a' : '#9ca3af',
                  color: 'white', cursor: conductAttendeeIds.length > 0 && !submitting ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', gap: '8px'
                }}
              >
                <FiCheck size={16} /> {submitting ? 'Completing...' : 'Complete Talk'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Safety;
